/**
 * File: Button.jsx
 * Path: /src/components/common/Button.jsx
 * Description: Reusable button component with multiple variants and states.
 * Key Features: Multiple color variants, sizes, states, and animation effects.
 * Maintenance Notes: Highly reusable component; extend when new button styles are needed.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const Button = ({
  children,
  to,
  href,
  variant = 'primary',
  size = 'md',
  className = '',
  onClick,
  icon,
  iconPosition = 'right',
  disabled = false,
  motionProps = {},
  ...rest
}) => {
  const baseClasses = "inline-flex items-center justify-center rounded-lg font-medium transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2";
  
  // Size classes
  const sizeClasses = {
    sm: "px-3 py-1.5 text-sm",
    md: "px-5 py-3 text-base",
    lg: "px-6 py-3.5 text-lg",
  };
  
  // Variant classes
  const variantClasses = {
    primary: "bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500 shadow-md hover:shadow-lg disabled:bg-primary-300",
    secondary: "bg-primary-50 text-primary-600 hover:bg-primary-100 focus:ring-primary-500 shadow-sm hover:shadow disabled:bg-primary-50 disabled:text-primary-300",
    outline: "bg-transparent text-primary-600 border border-primary-600 hover:bg-primary-50 focus:ring-primary-500 disabled:text-primary-300 disabled:border-primary-300",
    ghost: "bg-transparent text-primary-600 hover:bg-primary-50 focus:ring-primary-500 disabled:text-primary-300",
    dark: "bg-dark-800 text-white hover:bg-dark-700 focus:ring-dark-700 shadow-md hover:shadow-lg disabled:bg-dark-400",
    accent: "bg-accent-500 text-white hover:bg-accent-600 focus:ring-accent-400 shadow-md hover:shadow-lg disabled:bg-accent-300",
  };

  // Icon classes
  const iconClasses = icon ? (
    iconPosition === 'right' ? 'space-x-2' : 'flex-row-reverse space-x-reverse space-x-2'
  ) : '';

  const allClasses = `${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${iconClasses} ${className}`;

  // Render as Link if 'to' prop is provided
  if (to) {
    return (
      <motion.div whileTap={{ scale: 0.98 }} {...motionProps}>
        <Link
          to={to}
          className={allClasses}
          {...rest}
        >
          {icon && iconPosition === 'left' && <span>{icon}</span>}
          <span>{children}</span>
          {icon && iconPosition === 'right' && <span>{icon}</span>}
        </Link>
      </motion.div>
    );
  }
  
  // Render as anchor if 'href' prop is provided
  if (href) {
    return (
      <motion.div whileTap={{ scale: 0.98 }} {...motionProps}>
        <a
          href={href}
          className={allClasses}
          {...rest}
        >
          {icon && iconPosition === 'left' && <span>{icon}</span>}
          <span>{children}</span>
          {icon && iconPosition === 'right' && <span>{icon}</span>}
        </a>
      </motion.div>
    );
  }
  
  // Otherwise render as button
  return (
    <motion.button
      onClick={onClick}
      className={allClasses}
      disabled={disabled}
      whileTap={{ scale: 0.98 }}
      {...motionProps}
      {...rest}
    >
      {icon && iconPosition === 'left' && <span>{icon}</span>}
      <span>{children}</span>
      {icon && iconPosition === 'right' && <span>{icon}</span>}
    </motion.button>
  );
};

export default Button;